/**
 * ComponentStage - Container component for learning stages
 *
 * Manages session state and routes to VideoStage or QuizStage based on component type.
 * Handles session start API call, Focus Mode toggle, and ESC key binding.
 */

import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    learningSessionApi,
    type StartSessionResponse,
    type CompleteSessionResponse,
    type ComponentLocation,
} from '@/api/learningSession'
import { VideoStage } from './VideoStage'
import { QuizStage } from './QuizStage'
import { DiaryQuizStage } from './DiaryQuizStage'

// ============================================================================
// Types
// ============================================================================

export interface ComponentStageProps {
    /** Lesson ID to start session for */
    lessonId: number
    /** Lesson title for display */
    lessonTitle: string
    /** Component type: video, quiz, or assignment */
    componentType: 'video' | 'quiz' | 'assignment'
    /** Component location for API request */
    componentLocation: ComponentLocation
    /** Class product ID for B2B context */
    classProductId: number
    /** Required watch percent for auto-completion (from Configuration, default: 90) */
    requiredWatchPercent?: number
    /** Callback when component is completed */
    onComplete: (response: CompleteSessionResponse) => void
    /** Callback to go back to overview */
    onBack: () => void
    /** Callback to close/minimize sidebar (Focus Mode) */
    onSidebarClose: () => void
    /** Optional className */
    className?: string
}

type SessionState = 'loading' | 'ready' | 'error'

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert component location to quiz location format
 */
function toQuizLocation(location: ComponentLocation): 'pre' | 'post' | 'wrapup' | 'checkup' {
    switch (location) {
        case 'pre_quiz':
            return 'pre'
        case 'post_quiz':
            return 'post'
        case 'wrapup_quiz':
            return 'wrapup'
        case 'checkup_quiz':
            return 'checkup'
        default:
            return 'post'
    }
}

// ============================================================================
// Component
// ============================================================================

export function ComponentStage({
    lessonId,
    lessonTitle,
    componentType,
    componentLocation,
    classProductId,
    requiredWatchPercent,
    onComplete,
    onBack,
    onSidebarClose,
    className,
}: ComponentStageProps) {
    const { t } = useTranslation('learner')
    // Session state
    const [sessionState, setSessionState] = useState<SessionState>('loading')
    const [sessionData, setSessionData] = useState<StartSessionResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    // =========================================================================
    // ESC Key Handler
    // =========================================================================

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onBack()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [onBack])

    // =========================================================================
    // Focus Mode - Auto-close sidebar on mount
    // =========================================================================

    useEffect(() => {
        onSidebarClose()
    }, [onSidebarClose])

    // =========================================================================
    // Start Session on Mount
    // =========================================================================

    useEffect(() => {
        let isMounted = true

        const startSession = async () => {
            setSessionState('loading')
            setError(null)

            try {
                const response = await learningSessionApi.startSession({
                    lessonId,
                    componentType,
                    componentLocation,
                    classProductId,
                })

                if (isMounted) {
                    setSessionData(response)
                    setSessionState('ready')
                }
            } catch (err) {
                console.error('Failed to start session:', err)
                if (isMounted) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : t('learning.component.sessionStartFailed')
                    )
                    setSessionState('error')
                }
            }
        }

        startSession()

        return () => {
            isMounted = false
        }
    }, [lessonId, componentType, componentLocation, classProductId])

    // =========================================================================
    // Retry Handler
    // =========================================================================

    const handleRetry = useCallback(async () => {
        setSessionState('loading')
        setError(null)

        try {
            const response = await learningSessionApi.startSession({
                lessonId,
                componentType,
                componentLocation,
                classProductId,
            })

            setSessionData(response)
            setSessionState('ready')
        } catch (err) {
            console.error('Failed to start session:', err)
            setError(
                err instanceof Error
                    ? err.message
                    : t('learning.component.sessionStartFailed')
            )
            setSessionState('error')
        }
    }, [lessonId, componentType, componentLocation, classProductId])

    // =========================================================================
    // Render: Loading State
    // =========================================================================

    if (sessionState === 'loading') {
        return (
            <div
                className={cn(
                    'flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm',
                    className
                )}
            >
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
                <p className="mt-4 text-lg font-medium text-gray-700">{t('learning.component.preparing')}</p>
                <p className="mt-2 text-sm text-gray-500">{t('learning.component.pleaseWait')}</p>
            </div>
        )
    }

    // =========================================================================
    // Render: Error State
    // =========================================================================

    if (sessionState === 'error' || !sessionData) {
        return (
            <div
                className={cn(
                    'flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm',
                    className
                )}
            >
                <div className="flex items-center justify-center w-16 h-16 mb-6 bg-red-100 rounded-full">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="mb-2 text-xl font-bold text-gray-900">{t('learning.component.errorOccurred')}</h2>
                <p className="mb-6 text-center text-gray-600 max-w-md">
                    {error || t('learning.component.cannotStartSession')}
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onBack}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        {t('learning.component.goBack')}
                    </button>
                    <button
                        onClick={handleRetry}
                        className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        {t('learning.component.retry')}
                    </button>
                </div>
            </div>
        )
    }

    // =========================================================================
    // Render: Video Stage
    // =========================================================================

    if (componentType === 'video' && sessionData.videoData) {
        return (
            <div className={className}>
                <VideoStage
                    lessonId={lessonId}
                    lessonTitle={lessonTitle}
                    componentProgressId={sessionData.componentProgressId}
                    videoData={sessionData.videoData}
                    requiredWatchPercent={requiredWatchPercent}
                    classProductId={classProductId}
                    onComplete={onComplete}
                    onBack={onBack}
                />
            </div>
        )
    }

    // =========================================================================
    // Render: Quiz Stage
    // =========================================================================

    if (componentType === 'quiz' && sessionData.quizData) {
        return (
            <div className={className}>
                <QuizStage
                    lessonId={lessonId}
                    lessonTitle={lessonTitle}
                    componentProgressId={sessionData.componentProgressId}
                    sessionId={sessionData.sessionId ?? sessionData.componentProgressId}
                    quizData={sessionData.quizData}
                    quizLocation={toQuizLocation(componentLocation)}
                    classProductId={classProductId}
                    onComplete={onComplete}
                    onBack={onBack}
                    attemptNumber={sessionData.attemptNumber}
                />
            </div>
        )
    }

    // =========================================================================
    // Render: Diary Quiz Stage (Assignment with diaryQuizSetId)
    // =========================================================================

    if (componentType === 'assignment' && sessionData.diaryQuizData) {
        return (
            <div className={className}>
                <DiaryQuizStage
                    lessonId={lessonId}
                    lessonTitle={lessonTitle}
                    componentProgressId={sessionData.componentProgressId}
                    diaryQuizData={sessionData.diaryQuizData}
                    classProductId={classProductId}
                    onComplete={onComplete}
                    onBack={onBack}
                />
            </div>
        )
    }

    // =========================================================================
    // Render: Invalid Data State
    // =========================================================================

    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm',
                className
            )}
        >
            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-amber-100 rounded-full">
                <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">{t('learning.component.noData')}</h2>
            <p className="mb-6 text-center text-gray-600 max-w-md">
                {componentType === 'video'
                    ? t('learning.component.noVideoData')
                    : componentType === 'assignment'
                    ? t('learning.component.noDiaryData')
                    : t('learning.component.noQuizData')}
            </p>
            <button
                onClick={onBack}
                className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
                {t('learning.component.goBack')}
            </button>
        </div>
    )
}

export default ComponentStage
